import React, { useContext, useRef, useState } from 'react';
import { Chip, IconButton, Tooltip } from '@mui/material';
import { useDrag, useDrop } from 'react-dnd';

// ContextAPI
import { FormData, PopupContext } from '../../CreateQuestionnaire';

// Icons
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

export const FORM_ELEMENT_DRAG_TYPE = 'form-element';

// Constants
import { getUserSideFormTypeById } from '../../constants/formTypes.js';
import { POPUP_TYPES } from '../../constants/popupTypes';

// Services
import GraphStructureService from '../../services/graph.structurer.service.js';

import ConfirmDeleteDialog from '../ConfirmDeleteDialog';
import styles from './SectionComponent.module.css';

function FormComponent({ element, groupId, index, moveElement }) {
  const gs = new GraphStructureService();
  const [, setPopupContext] = useContext(PopupContext);
  const [formDataContext, setFormDataContext] = useContext(FormData);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const ref = useRef(null);

  const [, drop] = useDrop({
    accept: FORM_ELEMENT_DRAG_TYPE,
    hover(item, monitor) {
      if (!ref.current || item.groupId !== groupId) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      const hoverRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverRect.bottom - hoverRect.top) / 2;
      const hoverClientY = monitor.getClientOffset().y - hoverRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveElement(dragIndex, hoverIndex);
      item.index = hoverIndex;
    }
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: FORM_ELEMENT_DRAG_TYPE,
    item: () => ({ id: element.id, index, groupId }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() })
  });

  drop(preview(ref));

  const handleEdit = () => {
    setPopupContext({
      data: {
        id: groupId,
        formData: element,
        type: element.type
      },
      show: true,
      edit: true,
      type: POPUP_TYPES.ADD_FORM_ELEMENT
    });
  };

  const handleDelete = () => {
    setFormDataContext(
      gs.deleteFormElement(groupId, element.id, formDataContext)
    );
  };

  return (
    <div
      ref={ref}
      className={`${styles.ElementRow} ${isDragging ? styles.Dragging : ''}`}
    >
      <Tooltip title="Drag to reorder" arrow>
        <span ref={drag} className={styles.DragHandle}>
          <DragIndicatorIcon fontSize="small" />
        </span>
      </Tooltip>
      <div className={styles.ElementInfo}>
        <span className={styles.ElementLabel}>{element.label}</span>
        <Chip
          label={getUserSideFormTypeById(element.type)}
          size="small"
          className={styles.TypeChip}
        />
        {element.isRequired && (
          <Chip label="Required" size="small" className={styles.RequiredChip} />
        )}
      </div>
      <div className={styles.ElementActions}>
        <Tooltip title="Edit field" arrow>
          <IconButton size="small" onClick={handleEdit}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete field" arrow>
          <IconButton size="small" onClick={() => setConfirmOpen(true)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
      <ConfirmDeleteDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Delete field"
        message="Are you sure you want to delete this field?"
        itemName={element.label}
      />
    </div>
  );
}

export default FormComponent;
